import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Building2, Loader2 } from 'lucide-react'
import { orgAPI, authAPI, getApiBaseUrl } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useOrg } from '../context/OrgContext'
import { PENDING_ORG_INVITE_TOKEN_KEY } from '../constants/orgInvite'
import { resumeOAuthContextPath, consentResumeReturnTo } from '../context/ssoNavigation'
import { saveContinuation, loadContinuation, boundedContext, clearContinuation, appendContinuation } from '../context/oauthContinuation'

const PROVIDER_LABEL = { google: 'Google', microsoft: 'Microsoft' }
const PROVIDER_VALUE = { google: 'GoogleOAuth', microsoft: 'MicrosoftOAuth' }

/**
 * Public page: org invitation preview + accept/decline (auth required for actions).
 * Preserves ?token= through signup/login links.
 */
function JoinPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')?.trim() ?? ''
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { refetchOrg } = useOrg()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  // SSO onboarding options for a signed-out invitee (finding 2): global providers + whether the invite's org has company SSO.
  const [ssoMethods, setSsoMethods] = useState([])
  const [companyAvailable, setCompanyAvailable] = useState(false)

  const loadPreview = useCallback(async () => {
    if (!token) {
      setError('Missing invitation link. Use the link from your email.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await orgAPI.previewInvitation(token)
      const data = res?.data ?? res
      setPreview(data ?? null)
    } catch (e) {
      setError(e?.message || 'Could not load this invitation.')
      setPreview(null)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    loadPreview()
  }, [loadPreview])

  // Preserve any pending MCP OAuth consent context that arrived on this /join URL (a Connect flow deep-linked here) as a
  // BOUNDED, session-scoped continuation so it survives invitation SSO onboarding and resumes afterwards — mirrors
  // LoginPage. No-op unless the handle is actually present, so it never downgrades an existing good continuation.
  useEffect(() => {
    saveContinuation(typeof window !== 'undefined' ? window.location.search : '')
  }, [])

  const status = preview?.inviteStatus ?? preview?.status ?? ''
  const isPending = status === 'pending'
  // When the pending authorization is NOT actually in storage (unavailable / failed write), carry it on the login/signup
  // URLs too — so a signed-out invitee who signs in with a PASSWORD (not SSO) does not lose the Connect request (finding 1).
  // When it IS in storage, the links stay clean (the handle rides storage, scrubbed from analytics by analyticsPath).
  const joinFromUrl = boundedContext(typeof window !== 'undefined' ? window.location.search : '')
  const joinContinuation = joinFromUrl || loadContinuation() // URL wins over a possibly-stale stored authorization
  const carryJoinOnUrl = !!joinFromUrl // the current request's handle rides the /join URL (fallback) → keep carrying it
  const loginHref = carryJoinOnUrl ? appendContinuation(`/login?token=${encodeURIComponent(token)}`, joinContinuation) : `/login?token=${encodeURIComponent(token)}`
  const signupHref = carryJoinOnUrl ? appendContinuation(`/signup?token=${encodeURIComponent(token)}`, joinContinuation) : `/signup?token=${encodeURIComponent(token)}`

  // Once we know the invite's org (and it is still pending), ask the server which sign-in methods apply (finding 2/4). A
  // signed-out invitee can then onboard via SSO: the backend provisions them from THIS invite and signs them in.
  const orgId = preview?.orgId ?? ''
  useEffect(() => {
    if (!orgId || !isPending || isAuthenticated) { setSsoMethods([]); setCompanyAvailable(false); return }
    let cancelled = false
    authAPI.getSsoAvailability(orgId)
      .then((r) => {
        if (cancelled) return
        const methods = r?.data?.methods ?? r?.methods ?? []
        setSsoMethods(methods.filter((m) => m === 'google' || m === 'microsoft'))
        setCompanyAvailable(!!(r?.data?.company ?? r?.company))
      })
      .catch(() => { if (!cancelled) { setSsoMethods([]); setCompanyAvailable(false) } }) // flag off (404) → no SSO buttons
    return () => { cancelled = true }
  }, [orgId, isPending, isAuthenticated])

  // Start invitation-bound SSO: a full-page navigation to the backend SSO-start carrying the invite token. The callback
  // provisions/accepts from this pending invitation (strict email + org binding) and signs the user in. return_to preserves any
  // pending Connect authorization so it RESUMES after onboarding (not a hardcoded /dashboard — review R3-1); else the dashboard.
  const inviteReturnTo = () => consentResumeReturnTo((boundedContext(typeof window !== 'undefined' ? window.location.search : '') || loadContinuation())) || '/dashboard'
  const startInviteSso = (params) => {
    const base = getApiBaseUrl().replace(/\/+$/, '')
    const qs = new URLSearchParams({ invite_token: token, return_to: inviteReturnTo(), ...params })
    window.location.href = `${base}/auth/sso/start?${qs.toString()}`
  }

  const handleAccept = async () => {
    if (!token || !isAuthenticated) return
    setActionLoading(true)
    setError('')
    try {
      await orgAPI.acceptInvitation(token)
      try {
        localStorage.removeItem(PENDING_ORG_INVITE_TOKEN_KEY)
      } catch {
        /* ignore */
      }
      await refetchOrg()
      // Resume a pending MCP OAuth consent that was waiting on this membership (Finding 4); otherwise go to the dashboard.
      const resume = resumeOAuthContextPath((boundedContext(typeof window !== 'undefined' ? window.location.search : '') || loadContinuation()))
      if (resume) {
        clearContinuation()
        navigate(resume, { replace: true })
      } else {
        navigate('/dashboard', { replace: true })
      }
    } catch (e) {
      setError(e?.message || 'Could not accept the invitation.')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDecline = async () => {
    if (!token || !isAuthenticated) return
    setActionLoading(true)
    setError('')
    try {
      await orgAPI.declineInvitation(token)
      try {
        localStorage.removeItem(PENDING_ORG_INVITE_TOKEN_KEY)
      } catch {
        /* ignore */
      }
      clearContinuation() // gap 3: declining the invitation cancels the flow → drop the pending authorization too
      navigate('/dashboard', { replace: true })
    } catch (e) {
      setError(e?.message || 'Could not decline the invitation.')
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-teal-50/40 px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 shadow-sm"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-indigo-700 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Organization invitation</h1>
            <p className="text-sm text-gray-500">Review before you join</p>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading invitation…
          </div>
        )}

        {!loading && error && !preview && (
          <p className="text-sm text-red-600 mb-4">{error}</p>
        )}

        {!loading && preview && (
          <>
            {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

            <div className="space-y-3 text-gray-700 mb-6">
              <p>
                <span className="font-medium text-gray-900">{preview.inviterName || 'Someone'}</span>
                {' invited you to join '}
                <span className="font-semibold text-gray-900">{preview.orgName || 'an organization'}</span>
                {' on Rithmo'}
                {preview.role ? (
                  <>
                    {' as '}
                    <span className="capitalize">{preview.role.replace(/_/g, ' ')}</span>
                  </>
                ) : null}
                .
              </p>
              {preview.expiresAt && isPending && (
                <p className="text-xs text-gray-500">
                  Expires {new Date(preview.expiresAt).toLocaleString()}
                </p>
              )}
              {!isPending && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  This invitation is no longer active ({status}).
                </p>
              )}
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-100 px-4 py-3 text-sm text-slate-600 mb-6">
              By accepting, you join this organization in Rithmo. Organization admins may be able to see
              meetings and content shared in that organization, consistent with your settings and policies.
            </div>

            {!isAuthenticated && isPending && (
              <>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to={signupHref} className="btn-primary text-center">
                    Create account
                  </Link>
                  <Link to={loginHref} className="btn-secondary text-center">
                    Sign in
                  </Link>
                </div>

                {/* Single sign-on onboarding (finding 2): provision + sign in via the invitee's IdP, no password form. */}
                {(ssoMethods.length > 0 || companyAvailable) && (
                  <>
                    <div className="flex items-center gap-3 my-5">
                      <div className="h-px flex-1 bg-gray-200" />
                      <span className="text-xs text-gray-400">or continue with</span>
                      <div className="h-px flex-1 bg-gray-200" />
                    </div>
                    <div className="space-y-3">
                      {ssoMethods.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => startInviteSso({ provider: PROVIDER_VALUE[m] })}
                          className="w-full flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {PROVIDER_LABEL[m]}
                        </button>
                      ))}
                      {companyAvailable && (
                        <button
                          type="button"
                          onClick={() => startInviteSso({ company: '1' })}
                          className="w-full flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Building2 className="w-4 h-4" />
                          Company SSO
                        </button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {isAuthenticated && isPending && (
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={actionLoading}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Accept
                </button>
                <button
                  type="button"
                  onClick={handleDecline}
                  disabled={actionLoading}
                  className="btn-secondary"
                >
                  Decline
                </button>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-8 text-center">
              <Link to="/" className="underline hover:text-gray-600">
                Back to home
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  )
}

export default JoinPage
